import { useEffect, useState } from "react";
import { api } from "../../api";
import { useAuth } from "../../auth";
import { Card, Badge, Avatar, PageHead, Btn, Field, Input, Select } from "../../components/ui";
import Icon from "../../components/Icon";
import { BRAND, BRAND_TINT } from "../../theme";

// Returns true if a role should show the course-assignment picker
function needsCourseAssignment(role) {
  if (!role) return false;
  const name = role.name.toLowerCase();
  return name.includes("trainer") || name.includes("coordinator");
}

function EditUserModal({ target, roles, courses, privs, onClose, onSaved, toast, onEditRole }) {
  const [form, setForm] = useState({
    full_name: target.full_name || "",
    email: target.email || "",
    password: "",
    custom_role_id: target.custom_role_id ?? "",
    is_active: target.is_active !== false,
  });
  const [selCourses, setSelCourses] = useState(target.assigned_courses || []);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [section, setSection] = useState("general"); // general | courses | access

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const selectedRole = roles.find((r) => String(r.id) === String(form.custom_role_id));
  const isSuperAdmin = !form.custom_role_id;
  const rolePrivileges = isSuperAdmin ? privs.map((p) => p.key) : (selectedRole?.privileges || []);

  const toggleCourse = (cid) => {
    setSelCourses((prev) => prev.includes(cid) ? prev.filter((id) => id !== cid) : [...prev, cid]);
  };

  const save = async () => {
    setErr(""); setSaving(true);
    try {
      const body = {};
      if (form.full_name !== target.full_name) body.full_name = form.full_name;
      if (form.email !== target.email) body.email = form.email;
      if (form.password) body.password = form.password;
      const newRoleId = form.custom_role_id ? Number(form.custom_role_id) : null;
      if (newRoleId !== target.custom_role_id) body.custom_role_id = newRoleId;
      body.assigned_courses = selCourses;
      if (form.is_active !== (target.is_active !== false)) body.is_active = form.is_active;
      await api.updateUser(target.id, body);
      onSaved();
      toast("User updated");
    } catch (e) { setErr(e.message); } finally { setSaving(false); }
  };

  const tabs = [
    { key: "general", label: "General", icon: "users" },
    { key: "courses", label: "Courses", icon: "book" },
    { key: "access", label: "Access", icon: "shield" },
  ];

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <Card className="w-full max-w-xl p-0 bg-white max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-slate-100">
          <div>
            <div className="font-semibold text-lg text-slate-800">Edit User</div>
            <div className="text-sm text-slate-500 mt-0.5">{target.full_name || target.email}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><Icon name="x" size={20} /></button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-100 px-6">
          {tabs.map((t) => (
            <button key={t.key} onClick={() => setSection(t.key)}
              className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                section === t.key
                  ? "border-indigo-500 text-indigo-600"
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}>
              <Icon name={t.icon} size={14} />{t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {section === "general" && (
            <div className="space-y-4">
              <Field label="Full name"><Input value={form.full_name} onChange={set("full_name")} /></Field>
              <Field label="Email"><Input value={form.email} onChange={set("email")} /></Field>
              <Field label="New password (leave blank to keep current)"><Input value={form.password} onChange={set("password")} placeholder="min 6 characters" /></Field>
              <Field label="Role">
                <select value={form.custom_role_id} onChange={set("custom_role_id")}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500">
                  <option value="">Super Admin (all access)</option>
                  {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              </Field>
              {/* Active toggle */}
              <div className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3">
                <div>
                  <div className="text-sm font-medium text-slate-700">Account status</div>
                  <div className="text-xs text-slate-400 mt-0.5">{form.is_active ? "User can log in and access the system" : "User is blocked from logging in"}</div>
                </div>
                <button type="button" onClick={() => setForm({ ...form, is_active: !form.is_active })}
                  className={`relative w-11 h-6 rounded-full transition-colors ${form.is_active ? "bg-emerald-500" : "bg-slate-300"}`}>
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${form.is_active ? "translate-x-5" : "translate-x-0"}`} />
                </button>
              </div>
            </div>
          )}

          {section === "courses" && (
            <div>
              <div className="text-sm text-slate-500 mb-4">
                {isSuperAdmin
                  ? "Super Admins have access to all courses. Assign specific courses to limit access."
                  : `Select which courses this ${selectedRole?.name || "user"} can access.`}
              </div>
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-slate-700">{selCourses.length} of {courses.length} courses selected</span>
                <button onClick={() => setSelCourses(selCourses.length === courses.length ? [] : courses.map((c) => c.id))}
                  className="text-xs text-indigo-600 hover:text-indigo-800 font-medium">
                  {selCourses.length === courses.length ? "Deselect all" : "Select all"}
                </button>
              </div>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {courses.map((c) => (
                  <label key={c.id} className="flex items-center gap-3 rounded-lg border border-slate-200 px-3.5 py-2.5 cursor-pointer hover:bg-slate-50 transition-colors">
                    <input type="checkbox" checked={selCourses.includes(c.id)} onChange={() => toggleCourse(c.id)} className="accent-indigo-600 w-4 h-4" />
                    <div className="flex-1 min-w-0">
                      <span className="text-sm text-slate-700 block truncate">{c.name}</span>
                    </div>
                  </label>
                ))}
                {courses.length === 0 && <div className="text-sm text-slate-400 text-center py-6">No courses found</div>}
              </div>
              {selCourses.length === 0 && courses.length > 0 && (
                <div className="mt-3 text-xs text-amber-600 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                  No courses assigned — this user will have access to all courses.
                </div>
              )}
            </div>
          )}

          {section === "access" && (
            <div>
              <div className="text-sm text-slate-500 mb-4">
                {isSuperAdmin
                  ? "Super Admins have full access to all features."
                  : <>Permissions are set by the <strong>{selectedRole?.name}</strong> role. <button className="text-indigo-600 hover:underline" onClick={() => { onClose(); onEditRole(selectedRole); }}>Edit role</button></>
                }
              </div>
              <div className="space-y-1.5">
                {privs.map((p) => {
                  const has = rolePrivileges.includes(p.key);
                  return (
                    <div key={p.key} className={`flex items-center gap-3 rounded-lg px-3.5 py-2.5 text-sm ${has ? "bg-emerald-50 text-emerald-800" : "bg-slate-50 text-slate-400"}`}>
                      <Icon name={has ? "check" : "x"} size={14} />
                      <span>{p.label}</span>
                    </div>
                  );
                })}
              </div>
              {!isSuperAdmin && selectedRole && (
                <p className="text-xs text-slate-400 mt-4">
                  To change permissions, edit the "{selectedRole.name}" role in Roles & Permissions, or assign a different role.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/50 rounded-b-xl">
          {err && <div className="mb-3 text-sm text-red-600">{err}</div>}
          <div className="flex justify-end gap-2">
            <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
            <Btn onClick={save} disabled={saving}>{saving ? "Saving…" : "Save changes"}</Btn>
          </div>
        </div>
      </Card>
    </div>
  );
}

export default function Users({ toast }) {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [privs, setPrivs] = useState([]);
  const [q, setQ] = useState("");
  const [form, setForm] = useState({ full_name: "", email: "", password: "", custom_role_id: "" });
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [courses, setCourses] = useState([]);
  const [err, setErr] = useState("");
  const [tab, setTab] = useState("users"); // users | roles
  const [editRole, setEditRole] = useState(null); // { id?, name, privileges[] }
  const [editingUser, setEditingUser] = useState(null); // user object or null

  const load = () => {
    api.usersList().then((all) => setUsers(all.filter((u) => u.role !== "student"))).catch(() => {});
    api.listRoles().then(setRoles).catch(() => {});
  };
  useEffect(() => {
    load();
    api.listPrivileges().then(setPrivs).catch(() => {});
    api.courses().then(setCourses).catch(() => {});
  }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  // Check if the selected role needs course assignment (trainer or coordinator)
  const selectedRole = roles.find((r) => String(r.id) === String(form.custom_role_id));
  const showCoursePicker = needsCourseAssignment(selectedRole);

  const toggleCourse = (cid) => {
    setSelectedCourses((prev) => prev.includes(cid) ? prev.filter((id) => id !== cid) : [...prev, cid]);
  };

  const add = async () => {
    setErr("");
    try {
      const body = { full_name: form.full_name, email: form.email, password: form.password };
      if (form.custom_role_id) body.custom_role_id = Number(form.custom_role_id);
      if (showCoursePicker) body.assigned_courses = selectedCourses;
      await api.createUser(body);
      setForm({ full_name: "", email: "", password: "", custom_role_id: "" });
      setSelectedCourses([]);
      load(); toast("Administrator added");
    } catch (e) { setErr(e.message); }
  };
  const remove = async (u) => {
    if (!confirm(`Delete ${u.full_name || u.email}?`)) return;
    try { await api.deleteUser(u.id); load(); toast("User deleted"); } catch (e) { toast(e.message); }
  };
  const assignRole = async (uid, role_id) => {
    try { await api.assignRole(uid, role_id || null); load(); toast("Role updated"); } catch (e) { toast(e.message); }
  };
  const saveRole = async () => {
    try {
      if (editRole.id) { await api.updateRole(editRole.id, { name: editRole.name, privileges: editRole.privileges }); }
      else { await api.createRole(editRole.name, editRole.privileges); }
      setEditRole(null); load(); toast(editRole.id ? "Role updated" : "Role created");
    } catch (e) { toast(e.message); }
  };
  const deleteRole = async (id) => {
    if (!confirm("Delete this role? Users with this role will lose it.")) return;
    try { await api.deleteRole(id); load(); toast("Role deleted"); } catch (e) { toast(e.message); }
  };
  const togglePriv = (key) => {
    const p = editRole.privileges.includes(key) ? editRole.privileges.filter((k) => k !== key) : [...editRole.privileges, key];
    setEditRole({ ...editRole, privileges: p });
  };

  // Helper to get display text for assigned courses
  const courseNames = (ac) => {
    if (!ac || ac.length === 0) return null;
    return ac.map((id) => { const c = courses.find((x) => x.id === id); return c ? c.name : `#${id}`; }).join(", ");
  };

  return (
    <div>
      <PageHead title="Users" sub={`${users.length} admin account${users.length !== 1 ? "s" : ""}.`} />
      <div className="flex gap-2 mb-6">
        {["users", "roles"].map((t) => (
          <button key={t} onClick={() => setTab(t)} className="px-4 py-2 rounded-lg text-sm font-medium border"
            style={tab === t ? { backgroundColor: BRAND, color: "#fff", borderColor: BRAND } : { borderColor: "#d6d3d1", color: "#57534e" }}>
            {t === "users" ? "Admin Users" : "Roles & Permissions"}
          </button>
        ))}
      </div>

      {tab === "users" && (<>
        <Card className="p-5 mb-6">
          <div className="font-semibold text-slate-800 mb-4">Add an administrator</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Field label="Full name"><Input value={form.full_name} onChange={set("full_name")} placeholder="Jane Admin" /></Field>
            <Field label="Email"><Input value={form.email} onChange={set("email")} placeholder="jane@example.com" /></Field>
            <Field label="Password"><Input value={form.password} onChange={set("password")} placeholder="min 6 characters" /></Field>
            <Field label="Role">
              <select value={form.custom_role_id} onChange={set("custom_role_id")}
                className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500">
                <option value="">Super Admin (all access)</option>
                {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </Field>
          </div>
          {showCoursePicker && (
            <div className="mt-4">
              <div className="text-sm font-medium text-slate-700 mb-2">Assign courses this user can access</div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {courses.map((c) => (
                  <label key={c.id} className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 cursor-pointer hover:bg-slate-50">
                    <input type="checkbox" checked={selectedCourses.includes(c.id)} onChange={() => toggleCourse(c.id)} className="accent-indigo-600" />
                    <span className="text-sm text-slate-700">{c.name}</span>
                  </label>
                ))}
                {courses.length === 0 && <span className="text-sm text-slate-400">No courses found</span>}
              </div>
            </div>
          )}
          <div className="mt-4 flex items-center gap-3">
            <Btn onClick={add}><Icon name="plus" size={16} />Add user</Btn>
          </div>
          {err && <div className="mt-3 text-sm text-red-600">{err}</div>}
          <p className="text-xs text-slate-400 mt-2">New users get full admin access unless a custom role is assigned.</p>
        </Card>
        <div className="mb-3"><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search users by name or email…" /></div>
        <Card className="overflow-hidden"><div className="overflow-x-auto"><table className="w-full text-sm">
          <thead><tr className="text-left text-slate-500 border-b border-slate-200 bg-slate-50">
            <th className="px-4 py-3 font-medium">User</th><th className="px-4 py-3 font-medium">Role</th>
            <th className="px-4 py-3 font-medium">Courses</th>
            <th className="px-4 py-3 font-medium">Status</th><th className="px-4 py-3" />
          </tr></thead>
          <tbody>
            {users.filter((u) => !q || (u.full_name + " " + u.email).toLowerCase().includes(q.toLowerCase())).map((u) => {
              const uRole = roles.find((r) => r.id === u.custom_role_id);
              const showAssigned = needsCourseAssignment(uRole);
              return (
              <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-3"><div className="flex items-center gap-3"><Avatar name={u.full_name} size={32} /><div><div className="font-medium text-slate-900">{u.full_name || "—"}{u.id === user?.id && <span className="text-xs text-slate-400 ml-2">(you)</span>}</div><div className="text-xs text-slate-400">{u.email}</div></div></div></td>
                <td className="px-4 py-3">
                  <span className="text-sm text-slate-700">{u.custom_role_name || "Super Admin"}</span>
                </td>
                <td className="px-4 py-3">
                  {showAssigned && u.assigned_courses?.length > 0
                    ? <span className="text-xs text-slate-600">{courseNames(u.assigned_courses)}</span>
                    : showAssigned
                    ? <span className="text-xs text-slate-400">All courses</span>
                    : <span className="text-xs text-slate-300">—</span>
                  }
                </td>
                <td className="px-4 py-3"><Badge>{u.is_active ? "Active" : "Inactive"}</Badge></td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <Btn variant="ghost" onClick={() => setEditingUser(u)}><Icon name="edit" size={14} />Edit</Btn>
                    {u.id !== user?.id && <Btn variant="danger" onClick={() => remove(u)}>Delete</Btn>}
                  </div>
                </td>
              </tr>
            );})}
            {users.length === 0 && <tr><td colSpan="5" className="px-4 py-8 text-center text-slate-400">No admin users yet.</td></tr>}
          </tbody>
        </table></div></Card>
      </>)}

      {tab === "roles" && (<>
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-slate-500">Create roles with specific permissions, then assign them to admin users.</p>
          <Btn onClick={() => setEditRole({ name: "", privileges: [] })}><Icon name="plus" size={16} />New role</Btn>
        </div>
        {editRole && (
          <Card className="p-5 mb-6" style={{ borderColor: BRAND, borderWidth: 2 }}>
            <div className="font-semibold text-slate-800 mb-4">{editRole.id ? "Edit role" : "Create new role"}</div>
            <Field label="Role name"><Input value={editRole.name} onChange={(e) => setEditRole({ ...editRole, name: e.target.value })} placeholder="e.g. Academic Coordinator" /></Field>
            <div className="mt-4 mb-2 text-sm font-medium text-slate-700">Permissions</div>
            <div className="grid sm:grid-cols-2 gap-2">
              {privs.map((p) => (
                <label key={p.key} className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 cursor-pointer hover:bg-slate-50">
                  <input type="checkbox" checked={editRole.privileges.includes(p.key)} onChange={() => togglePriv(p.key)} className="accent-indigo-600" />
                  <span className="text-sm text-slate-700">{p.label}</span>
                </label>
              ))}
            </div>
            <div className="flex gap-2 mt-4">
              <Btn onClick={saveRole}>{editRole.id ? "Save changes" : "Create role"}</Btn>
              <Btn variant="ghost" onClick={() => setEditRole(null)}>Cancel</Btn>
              <div className="flex-1" />
              <Btn variant="ghost" onClick={() => { const all = privs.map((p) => p.key); setEditRole({ ...editRole, privileges: editRole.privileges.length === all.length ? [] : all }); }}>
                {editRole.privileges.length === privs.length ? "Deselect all" : "Select all"}
              </Btn>
            </div>
          </Card>
        )}
        <div className="space-y-3">
          {roles.map((r) => (
            <Card key={r.id} className="p-5">
              <div className="flex items-center justify-between mb-2">
                <div><span className="font-semibold text-slate-800">{r.name}</span><span className="text-xs text-slate-400 ml-2">{r.user_count} user{r.user_count !== 1 ? "s" : ""}</span></div>
                <div className="flex gap-2">
                  <Btn variant="ghost" onClick={() => setEditRole({ id: r.id, name: r.name, privileges: [...r.privileges] })}>Edit</Btn>
                  <Btn variant="danger" onClick={() => deleteRole(r.id)}>Delete</Btn>
                </div>
              </div>
              <div className="flex flex-wrap gap-1">
                {r.privileges.map((k) => { const p = privs.find((x) => x.key === k); return p ? <span key={k} className="text-xs rounded-full px-2 py-0.5 bg-slate-100 text-slate-600">{p.label}</span> : null; })}
                {r.privileges.length === 0 && <span className="text-xs text-slate-400">No permissions assigned</span>}
              </div>
            </Card>
          ))}
          {roles.length === 0 && !editRole && <div className="text-sm text-slate-400 text-center py-8">No custom roles yet. Create one to get started.</div>}
        </div>
      </>)}

      {editingUser && (
        <EditUserModal
          target={editingUser}
          roles={roles}
          courses={courses}
          privs={privs}
          onClose={() => setEditingUser(null)}
          onSaved={() => { setEditingUser(null); load(); }}
          toast={toast}
          onEditRole={(role) => { setTab("roles"); setEditRole({ id: role.id, name: role.name, privileges: [...role.privileges] }); }}
        />
      )}
    </div>
  );
}
